import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Task } from '../models/Task';
import { parseTaskVersion } from '../constants/taskVersionMilestone';
import { displayTaskTitle } from '../utils/taskTitle';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../models/User';

const parseDateOnlyAsLocalDate = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
};

export function useTasks(clientId?: string | null) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    setLoading(true);

    let q;
    let needsSorting = false;
    if (clientId) {
      q = query(
        collection(db, 'tasks'),
        where('clientId', '==', clientId)
      );
      needsSorting = true;
    } else if (currentUser?.role === UserRole.SUPER_ADMIN) {
      q = query(
        collection(db, 'tasks'),
        orderBy('createdAt', 'desc')
      );
    } else {
      setTasks([]);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const taskData = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          const { labels: rawLabels, ...rest } = data as Record<string, unknown>;
          const labels = Array.isArray(rawLabels)
            ? rawLabels.filter((l): l is string => typeof l === 'string')
            : undefined;
          return {
            id: docSnap.id,
            ...rest,
            title: displayTaskTitle(data.title),
            clientId: data.clientId || '',
            projectId: data.projectId || null,
            dueDate: data.dueDate?.toDate(),
            estimatedHours:
              data.estimatedHours == null
                ? null
                : Number.isFinite(Number(data.estimatedHours))
                  ? Number(data.estimatedHours)
                  : null,
            version: parseTaskVersion(data.version),
            ...(labels !== undefined ? { labels } : {}),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Task;
        });

        if (needsSorting) {
          taskData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        }

        setTasks(taskData);
        setLoading(false);
        setError(null);
      },
      (err: any) => {
        const errorMessage = err?.message || '';
        if (err?.code === 'failed-precondition' && (errorMessage.includes('index') || errorMessage.includes('Index'))) {
          setError(new Error('Database indexes are currently building. Please wait a few minutes and refresh the page.'));
        } else {
          setError(err as Error);
        }
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [clientId, currentUser?.role]);

  const convertDatesToTimestamps = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (obj instanceof Date) return Timestamp.fromDate(obj);
    if (obj instanceof Timestamp) return obj;
    if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}/.test(obj)) {
      const asLocalDate = parseDateOnlyAsLocalDate(obj);
      return Timestamp.fromDate(asLocalDate ?? new Date(obj));
    }
    if (Array.isArray(obj)) return obj.map((item) => convertDatesToTimestamps(item));
    if (typeof obj === 'object') {
      const converted: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          converted[key] = convertDatesToTimestamps(obj[key]);
        }
      }
      return converted;
    }
    return obj;
  };

  /** Firestore rejects `undefined` anywhere in the payload (including nested objects). */
  const omitUndefinedDeep = (value: unknown): unknown => {
    if (value === undefined) {
      return undefined;
    }
    if (value === null || value instanceof Date || value instanceof Timestamp) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((item) => omitUndefinedDeep(item));
    }
    if (typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [key, child] of Object.entries(value)) {
        const next = omitUndefinedDeep(child);
        if (next !== undefined) {
          out[key] = next;
        }
      }
      return out;
    }
    return value;
  };

  const createTask = async (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    const now = Timestamp.now();
    const convertedData = omitUndefinedDeep(convertDatesToTimestamps(taskData)) as Record<string, unknown>;
    const clientId = convertedData.clientId as string | undefined;
    const projectId = convertedData.projectId as string | undefined;
    const ref = await addDoc(collection(db, 'tasks'), {
      ...convertedData,
      clientId: clientId || '',
      projectId: projectId || null,
      createdAt: now,
      updatedAt: now,
    });
    return ref.id;
  };

  const updateTask = async (taskId: string, updates: Partial<Task>) => {
    const convertedUpdates = omitUndefinedDeep(convertDatesToTimestamps(updates)) as Record<string, unknown>;
    const resolvedClientId = convertedUpdates.clientId as string | undefined;
    const resolvedProjectId = convertedUpdates.projectId as string | undefined;
    const relationFields: Record<string, unknown> = {};
    if (resolvedClientId !== undefined) {
      relationFields.clientId = resolvedClientId;
    }
    if (resolvedProjectId !== undefined) {
      relationFields.projectId = resolvedProjectId;
    }
    const taskRef = doc(db, 'tasks', taskId);
    await updateDoc(taskRef, {
      ...convertedUpdates,
      ...relationFields,
      updatedAt: Timestamp.now(),
    });
  };

  const deleteTask = async (taskId: string) => {
    const taskRef = doc(db, 'tasks', taskId);
    await deleteDoc(taskRef);
  };

  return {
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    deleteTask,
  };
}
